import { ChevronDownIcon, ChevronRightIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { isEmpty } from 'radash';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { SectionValidationPopover } from '@/dialogs/item-editor/tabs/ItemEditorTab/components/SectionValidationPopover';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute } from '@/types/DataModel';

import { ReactComponent as squareArrowTurnIcon  } from '../../../assets/icons/square-arrow-turn.svg';
import { useGroupTitle } from '../hooks/useGroupTitle';
import { useValidationsStore, Validation } from '../hooks/useValidationsStore';
import GroupDeleteConfirmationDialog from './GroupDeleteConfirmationDialog';

const GroupTitleComponent = ({
    attribute,
    fieldPath,
    index,
    onTitleClick,
    isExpanded,
    memberAttributes,
    bgClore,
    onRemove,
    onDuplicate,
    actionEnabled,
    readonly,
    onSetEntry
}: {
    attribute: DataModelAttribute;
    fieldPath: string;
    index: number;
    bgClore: string;
    memberAttributes: any; //FIXME: Type
    onTitleClick: (i: number) => void;
    isExpanded: boolean;
    readonly: boolean
    onRemove: any ;
    onDuplicate: any ;
    actionEnabled:any ;
    onSetEntry: any;
}) => {

    const { t } = useTranslation();
    const currentValue = useWatch({ name: fieldPath });
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const getEntryTitle = useGroupTitle({
        attribute,
        index,
        memberAttributes,
        currentValue,
    });

    const validationErrors = useValidationsStore(
        useShallow((state) => state.validationErrors[fieldPath] as Validation[]),
    );

    const hasMatchingIndex = (index: number, validationErrors: Validation[]) => {
        if (!isEmpty(validationErrors)) {
            return validationErrors.some((error) => error.path[1] === index);
        }
    };

    return (
        <div
            className={`group-title tw-select-none ${bgClore} tw-rounded-sm ${
                hasMatchingIndex(index,validationErrors) ? 'error-group-title' : ''
            }`}
        >
            <div
                className="tw-flex tw-items-center tw-justify-between tw-h-[40px] tw-w-full tw-cursor-pointer"
                onClick={() => onTitleClick(index)}
            >
                <div className="tw-flex tw-gap-2 tw-overflow-hidden tw-p-2">
                    <Icon
                        className="tw-p-1 text-xs"
                        icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                        size='sm'
                    />
                    <span>
                        {index + 1} / {currentValue?.length}
                    </span>
                    <span className="group-title-label tw-max-w-[25ch] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                        {parse(getEntryTitle ?? '')}
                    </span>

                    {hasMatchingIndex(index, validationErrors) && (
                        <>
                            <SectionValidationPopover sectionAttributeNames={['group']} type="errors" />
                            <SectionValidationPopover sectionAttributeNames={['group']} type="warnings" />
                        </>
                    )}
                </div>

                <div className="tw-flex tw-items-center">
                    {(actionEnabled('copyEntry')) && (
                        <IconButton
                            type="button"
                            icon={DocumentDuplicateIcon}
                            disabled={readonly}
                            onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
                            title={t('GROUP_DUPLICATE')}
                        />
                    )}
                    {actionEnabled('setEntry') && <IconButton
                        type="button"
                        icon={squareArrowTurnIcon}
                        disabled={readonly}
                        onClick={(e) => { e.stopPropagation(); onSetEntry(index); }}
                        title={t('GROUP_REPLACE')}
                    />}
                    {actionEnabled('deleteEntry') && <IconButton
                        type="button"
                        icon={TrashIcon}
                        disabled={readonly}
                        onClick={(e) => { e.stopPropagation(); setIsDialogOpen(true); }}
                        className="delete"
                        variant='destructive'
                        title={t('GROUP_DELETE_ENTRY')}
                    />}
                    {isDialogOpen && (
                        <GroupDeleteConfirmationDialog
                            open={isDialogOpen}
                            onRemove={() => onRemove(index)}
                            onClose={() => setIsDialogOpen(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupTitleComponent;
